import '../../pages/conversations/MessagesPage.css';
import { useEffect, useState, useRef } from 'react';
import { useMessagesFeedSocket } from './useMessagesFeedSocket';
import axios from 'axios';
import config from '../../config';
import InfiniteScroll from 'react-infinite-scroll-component';
import Message from '../message/Message';
import Popup from '../popup/Popup';
import PropTypes from 'prop-types';
import React from 'react';
import ReactLoading from 'react-loading';

const PAGE_SIZE = 25;

// function to fetch messages using page number
const fetchMessages = async (conversationId, page) => {
  const token = localStorage.getItem("token");
  const res = await axios.get(`${config.apiUrl}/messages/${conversationId}?page=${page}`, {
      headers: {
          'x-auth-token': token
      }
  })
  const data = res.data;
  const markedAsRead = data.markedAsRead || 0;
  const messages = data.messages || [];
  const hasMore = messages.length === PAGE_SIZE;
  return { messages: messages, hasMore, markedAsRead: markedAsRead };
};

export default function MessagesFeed({ conversationId, setUnreadMessageCount }) {
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allMessagesLoaded, setAllMessagesLoaded] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  // state attached to the last message, ensures last messages is viewable
  const lastMessageRef = useRef(null);
  // state to determine if the last message was sent by the user or just loaded
  const [isLastMessageSentByUser, setIsLastMessageSentByUser] = useState(false);
  // state to determine if the scroll into view hook should run 
  const [runScrollIntoView, setRunScrollIntoView] = useState(false);
  
  // used for catch blocks, error message
  const popupStaus = async (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  }

  // load initial messages for the conversation
  useEffect(() => {
    const loadInitialMessages = async () => {
      const { messages: newMessages, hasMore: newHasMore, markedAsRead: markedAsRead  } = await fetchMessages(conversationId, 1);
      setUnreadMessageCount(prev => Math.max(0, prev - markedAsRead));
      setMessages(newMessages);
      setHasMore(newHasMore);
      setAllMessagesLoaded(true);
    };
    setPage(1);
    setMessages([]);
    setHasMore(true);
    loadInitialMessages();
  }, [conversationId]);

  // used by infinite scroll component, calls fetchMessages function outside component
  const loadMoreMessages = async () => {
    // prevent fetching if no more messages
    if (!hasMore) return; 
    try {    
      const nextPage = page + 1;
      const { messages: newMessages, hasMore: newHasMore, markedAsRead: markedAsRead } = await fetchMessages(conversationId, nextPage);
      setUnreadMessageCount(prev => Math.max(0, prev - markedAsRead));
      setMessages((prevMessages) => [...prevMessages, ...newMessages]);
      setPage(nextPage);
      setHasMore(newHasMore);
    } catch {
      await popupStaus("An error occured when fetching messages. Please try again later.")
    }
  };

  // scroll into view after sending a message
  useEffect(() => {
    if (lastMessageRef.current && isLastMessageSentByUser) {
      lastMessageRef.current.scrollIntoView({
        behavior: 'smooth', 
        block: 'end',
      });
    }
    setIsLastMessageSentByUser(false);
    setRunScrollIntoView(false);
    // depend on messages to trigger scroll when it updates.
  }, [messages, runScrollIntoView]); 

  // LOGIC FOR SOCKET HERE
  useMessagesFeedSocket(conversationId, setMessages, setIsLastMessageSentByUser, setRunScrollIntoView, isLastMessageSentByUser, messages);

return (
  <div className='messages-feed-infinite-container' id="messagesInfiniteScroll">
    <Popup isPopupOpen={showPopup} message={popupMessage} button1Text="Close" button1Action={() => setShowPopup(false)} />
      {!allMessagesLoaded ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10rem', marginBottom: '20px' }}>
          <ReactLoading type={'balls'} color={'white'} height={'20%'} width={'20%'} />
        </div>
      ) : (
          <InfiniteScroll
            style={{ display: 'flex', flexDirection: 'column-reverse'}}
            inverse={true}
            dataLength={messages.length}
            scrollableTarget="messagesInfiniteScroll"
            next={loadMoreMessages}
            hasMore={hasMore}
            page={page}
          >
            {messages.map((m, index) => (
              <Message 
                key={m._id + '-' + index} 
                message={m} 
                ref={index === 0 ? lastMessageRef : null}
                />
            ))}
          </InfiniteScroll>
        )
      }
  </div>
  )
}
MessagesFeed.propTypes = {
  conversationId: PropTypes.string,
  setUnreadMessageCount: PropTypes.func,
}